import { useMemo } from 'react';
import type { DocumentRenderAttachmentSummary, TemplateEntryDef } from '@vivport/backend/types';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { EMPTY_CONTROL_STATE, OTHER_OPTION, resolveControlValue } from './field-control-state';
import { categoryItems, type EntryFormState } from './entry-form-state';
import { groupFieldsInDefOrder } from './entry-groups';
import type { EntryStep } from './entry-steps';
import { TemplateEntryPhotoPicker } from './TemplateEntryPhotoPicker';
import { TemplateFieldControl } from './TemplateFieldControl';

interface EntryFormFieldsProps {
  def: TemplateEntryDef;
  state: EntryFormState;
  onChange: (patch: Partial<EntryFormState>) => void;
  sectionId: number;
  photos: DocumentRenderAttachmentSummary[];
  existingPhotoCodes: string[];
  capture: boolean;
  disabled: boolean;
  idPrefix: string;
  pickerKey: number;
  /** Omitted renders every section, exactly as before. Supplied renders only that step. */
  step?: EntryStep;
}

// One body for the page dialog and the chat card: categories, items, groups, control types and
// options are read straight from the definition; the tool validates every value on submit.
export function EntryFormFields({
  def,
  state,
  onChange,
  sectionId,
  photos,
  existingPhotoCodes,
  capture,
  disabled,
  idPrefix,
  pickerKey,
  step,
}: EntryFormFieldsProps) {
  const groups = useMemo(() => groupFieldsInDefOrder(def), [def]);
  const categories = def.categories ?? [];
  const hasCategories = categories.length > 0;
  const categoryChosen = categories.some((candidate) => candidate.label === state.category);
  const items = categoryItems(def, state.category);
  const titleAsSelect = items.length > 0;
  const customAllowed = def.allowCustomItem === true || !hasCategories;

  const showIdentity = step === undefined || step.kind === 'identity';
  const showPhotos = step === undefined || step.kind === 'identity';
  const showNotes = step === undefined || step.kind === 'review';
  const visibleGroups = step === undefined ? groups : step.kind === 'group' ? [{ group: step.group, fields: step.fields }] : [];

  const reviewRows =
    step !== undefined && step.kind === 'review'
      ? [
          ...(hasCategories ? [{ label: 'Category', value: state.category }] : []),
          { label: def.itemLabel, value: resolveControlValue(state.title) },
          ...def.fields.map((field) => ({
            label: field.label,
            value: resolveControlValue(state.drafts[field.key] ?? EMPTY_CONTROL_STATE),
          })),
          ...(def.photos ? [{ label: 'Photos', value: `${state.photoIds.length} selected` }] : []),
        ].filter((row) => row.value.length > 0)
      : [];

  return (
    <>
      {showIdentity && hasCategories ? (
        <div className="space-y-1.5 max-sm:scroll-mt-20">
          <Label htmlFor={`${idPrefix}-category`}>Category</Label>
          <Select
            value={state.category}
            onValueChange={(value) => onChange({ category: value, title: EMPTY_CONTROL_STATE })}
            disabled={disabled}
          >
            <SelectTrigger id={`${idPrefix}-category`} className="h-8 max-sm:h-12 max-sm:text-base">
              <SelectValue placeholder="Choose…" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.label} value={category.label} className="max-sm:min-h-12 max-sm:text-base">
                  {category.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      {showIdentity ? (
      <div className="space-y-1.5 max-sm:scroll-mt-20">
        <Label htmlFor={`${idPrefix}-title`}>{def.itemLabel}</Label>
        {titleAsSelect ? (
          <div className="space-y-2">
            <Select
              value={state.title.value}
              onValueChange={(value) => onChange({ title: { ...state.title, value } })}
              disabled={disabled}
            >
              <SelectTrigger id={`${idPrefix}-title`} className="h-8 max-sm:h-12 max-sm:text-base">
                <SelectValue placeholder="Choose…" />
              </SelectTrigger>
              <SelectContent>
                {items.map((item) => (
                  <SelectItem key={item} value={item} className="max-sm:min-h-12 max-sm:text-base">
                    {item}
                  </SelectItem>
                ))}
                {customAllowed ? (
                  <SelectItem value={OTHER_OPTION} className="max-sm:min-h-12 max-sm:text-base">
                    Other…
                  </SelectItem>
                ) : null}
              </SelectContent>
            </Select>
            {state.title.value === OTHER_OPTION ? (
              <Input
                value={state.title.other}
                onChange={(event) => onChange({ title: { ...state.title, other: event.target.value } })}
                placeholder="Name"
                disabled={disabled}
                className="h-8 max-sm:h-12 max-sm:text-base"
              />
            ) : null}
          </div>
        ) : (
          <Input
            id={`${idPrefix}-title`}
            value={state.title.value}
            onChange={(event) => onChange({ title: { value: event.target.value, other: '' } })}
            disabled={disabled || (hasCategories && !categoryChosen)}
            placeholder={hasCategories && !categoryChosen ? 'Choose a category first' : 'Name'}
            className="h-8 max-sm:h-12 max-sm:text-base"
          />
        )}
      </div>
      ) : null}
      {visibleGroups.map(({ group, fields }) => (
        <div key={group ?? ''} className="space-y-2">
          {group !== null ? (
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">{group}</p>
          ) : null}
          {fields.map((field) => {
            const controlId = `${idPrefix}-${field.key}`;
            return (
              <div key={field.key} className="space-y-1.5 max-sm:scroll-mt-20">
                <Label htmlFor={controlId} className="flex items-center gap-1.5">
                  {field.label}
                  {field.required === true ? <Badge variant="outline">required</Badge> : null}
                </Label>
                <TemplateFieldControl
                  id={controlId}
                  spec={field}
                  state={state.drafts[field.key] ?? EMPTY_CONTROL_STATE}
                  onChange={(next) => onChange({ drafts: { ...state.drafts, [field.key]: next } })}
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>
      ))}
      {showPhotos && def.photos ? (
        <div className="space-y-1.5">
          <Label>Photos</Label>
          {existingPhotoCodes.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {existingPhotoCodes.map((code) => {
                const known = photos.find((photo) => photo.code === code);
                return (
                  <Badge key={code} variant="secondary">
                    {known !== undefined && known.title.length > 0 ? known.title : code}
                  </Badge>
                );
              })}
            </div>
          ) : null}
          <TemplateEntryPhotoPicker
            key={pickerKey}
            sectionId={sectionId}
            photos={photos.filter((photo) => !existingPhotoCodes.includes(photo.code))}
            selectedIds={state.photoIds}
            onChange={(ids) => onChange({ photoIds: ids })}
            disabled={disabled}
            idPrefix={idPrefix}
            capture={capture}
          />
        </div>
      ) : null}
      {showNotes && def.notes ? (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-notes`}>Notes</Label>
          <Textarea
            id={`${idPrefix}-notes`}
            className="max-sm:text-base"
            rows={3}
            value={state.notes}
            onChange={(event) => onChange({ notes: event.target.value })}
            disabled={disabled}
          />
        </div>
      ) : null}
      {step !== undefined && step.kind === 'review' ? (
        <dl className="space-y-2 rounded-md border p-3 text-sm">
          {reviewRows.length === 0 ? (
            <p className="text-muted-foreground">Nothing filled in yet.</p>
          ) : (
            reviewRows.map((row) => (
              <div key={row.label} className="flex justify-between gap-3">
                <dt className="text-muted-foreground">{row.label}</dt>
                <dd className="text-right font-medium">{row.value}</dd>
              </div>
            ))
          )}
        </dl>
      ) : null}
    </>
  );
}
